/**
 * Created by RSchweber-Koren on 4/10/2017.
 */

import Assert from "tbAssert";
import MakePropsEnumerable from "tbClient/utility/MakePropsEnumerable";
import StringUtils from "tbClient/utility/StringUtils";

//import TbAppConfigError from "./TbAppConfigError";

class TbAppConfigInternal {
    constructor({
        configurationName: appConfigName,
        oneLoginServer: oneLoginServer,
        apiServerBaseUrl: serverBaseUrl,
        apiAuthId: apiAudienceId,
        authConnectionType: authConnType,
        authConnectionName: authConnName,
        appUrl: clientAppUrl,
        clientAuthId: clientAuthId,
        enableDebugMode: showDebug,
        appInsightsApiKey: aiApiKey,
        showVersionLabel: showVerLabel
    }) {
        Assert.StrictAssert(appConfigName != null, "Missing or invalid application configuration name");
        Assert.StrictAssert(oneLoginServer != null, "Missing or invalid OneLogin server");
        Assert.StrictAssert(serverBaseUrl != null, "Missing or invalid apiServerBaseUrl");
        Assert.StrictAssert(apiAudienceId != null, "Missing or invalid api id" );
        Assert.StrictAssert(authConnType != null, "Missing or invalid auth connection type");
        Assert.StrictAssert(clientAppUrl != null, "Missing or invalid appUrl");
        Assert.StrictAssert(clientAuthId != null, "Missing or invalid clientAuthId");
        Assert.StrictAssert(aiApiKey != null, "Missing or invalid appInsightsApiKey");

        this._tbAppConfigName = appConfigName;
        this._tbOneLoginServer = oneLoginServer;
        this._tbServerBaseUrl = serverBaseUrl;
        this._tbAuthApiId = apiAudienceId;
        this._tbAuthConnType = authConnType;
        this._tbAuthConnName = StringUtils.IsNullOrWhitespace(authConnName) ? null : authConnName;
        this._tbClientAppUrl = clientAppUrl;
        this._tbOneLoginClientId = clientAuthId;
        this._tbEnableDebugMode = (showDebug === true);
        this._tbAppInsightsApiKey = aiApiKey;
        this._tbShowVersionLabel = (showVerLabel === true);
        this._tbLogoutUrl = `https://${this._tbOneLoginServer}/oidc/2/logout`;
    }

    toString() {
        let parts = [
            `TrackerBase Application Configuration (${this._tbAppConfigName}):`,
            `Debug Mode: ${ this._tbEnableDebugMode ? "ON" : "OFF"}`,
            `OIDC Auth Server: ${this._tbOneLoginServer}`,
            `Server Api Base Url: ${this._tbServerBaseUrl}`,
            `OIDC Api Audience Id: ${this._tbAuthApiId}`,
            `OneLogin Connection Type: ${this._tbAuthConnType}`,
            `OneLogin Connection Name: ${this._tbAuthConnName || "(None)"}`,
            `Client Base Url: ${this._tbClientAppUrl}`,
            `OIDC Client Id: ${this._tbOneLoginClientId}`,
            `Application Insights Key: ${this._tbAppInsightsApiKey}`,
            `Show Version Label? ${this._tbShowVersionLabel ? "Yes" : "No" }`,
            `Logout Url: ${this._tbLogoutUrl}`
        ];

        return parts.join("\n");
    }
}

const mTbAppConfigData = new WeakMap();

class TbAppConfig {
    constructor({
        tbAppConfigName: configName,
        tbOneLoginServer: oneLoginServer,
        tbServerBaseUrl: serverBaseUrl,
        tbApiId: apiAuthAudienceId,
        tbAuthConnectionType: authConnectionType,
        tbAuthConnectionName: authConnectionName,
        tbClientAppUrl: clientAppUrl,
        tbOneLoginClientId: oneLoginClientId,
        tbEnableDebugMode: enableDebugMode,
        tbAppInsightsApiKey: appInsightsKey,
        tbShowVersionLabel: showVersionLabel
    }) {
        Assert.StrictAssert(configName != null, "Missing or invalid application configuration data object");
        const appConfigData = new TbAppConfigInternal({
            configurationName: configName,
            oneLoginServer: oneLoginServer,
            apiServerBaseUrl: serverBaseUrl,
            apiAuthId: apiAuthAudienceId,
            authConnectionType: authConnectionType,
            authConnectionName: authConnectionName,
            appUrl: clientAppUrl,
            clientAuthId: oneLoginClientId,
            enableDebugMode: enableDebugMode,
            appInsightsApiKey: appInsightsKey,
            showVersionLabel: showVersionLabel
        });
        mTbAppConfigData.set(this, appConfigData);
    }

    //tbAppConfigName - the name for the configuration
    get tbAppConfigName() {
        return mTbAppConfigData.get(this)._tbAppConfigName;
    }

    //tbOneLoginServer - the base domain for the OneLogin authentication server
    get tbOneLoginServer() {
        return mTbAppConfigData.get(this)._tbOneLoginServer;
    }

    // Legacy getter for backwards compatibility during transition
    get tbAuthServer() {
        return this.tbOneLoginServer;
    }

    //tbApiServerUrl - the base url for the api server.
    get tbServerBaseUrl() {
        return mTbAppConfigData.get(this)._tbServerBaseUrl;
    }

    get tbAuthApiId() {
        return mTbAppConfigData.get(this)._tbAuthApiId;
    }

    get tbAuthConnectionType() {
        return mTbAppConfigData.get(this)._tbAuthConnType;
    }

    get tbAuthConnectionName() {
        return mTbAppConfigData.get(this)._tbAuthConnName;
    }

    //tbClientAppUrl - the base url for the frontend client
    get tbClientAppUrl() {
        return mTbAppConfigData.get(this)._tbClientAppUrl;
    }

    //tbOneLoginClientId - OneLogin Client Id
    get tbOneLoginClientId() {
        return mTbAppConfigData.get(this)._tbOneLoginClientId;
    }

    // Legacy getter for backwards compatibility during transition
    get tbAuthClientId() {
        return this.tbOneLoginClientId;
    }

    get tbLogoutUrl() {
        return mTbAppConfigData.get(this)._tbLogoutUrl;
    }

    //tbDebugMode - enable debugging code throughout the app.
    get tbEnableDebugMode() {
        return mTbAppConfigData.get(this)._tbEnableDebugMode;
    }

    //tbAppInsigntsApiKey
    // Application insights analytics instrumentation key
    get tbAppInsightsApiKey() {
        return mTbAppConfigData.get(this)._tbAppInsightsApiKey;
    }

    //tbShowVersionLabel
    // Show the version label name on the homepage
    get tbShowVersionLabel() {
        return mTbAppConfigData.get(this)._tbShowVersionLabel;
    }

    toString() {
        const appConfigData = mTbAppConfigData.get(this);
        if (appConfigData != null) {
            return appConfigData.toString();
        }
        else {
            return "Unconfigured";
        }
    }
}

MakePropsEnumerable(TbAppConfig.prototype, [
    "tbAppConfigName",
    "tbOneLoginServer",
    "tbAuthServer", // Legacy compatibility
    "tbServerBaseUrl",
    "tbAuthApiId",
    "tbAuthConnectionType",
    "tbAuthConnectionName",
    "tbClientAppUrl",
    "tbOneLoginClientId",
    "tbAuthClientId", // Legacy compatibility
    "tbLogoutUrl",
    "tbEnableDebugMode",
    "tbAppInsightsApiKey",
    "tbShowVersionLabel"
]);

export default TbAppConfig;




//////////////////
// WEBPACK FOOTER
// ./src/config/tb-app-config/TbAppConfig.js
// module id = x/ba
// module chunks = 1